import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, Check, LogOut, Download } from "lucide-react";
import { toast } from "sonner";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { api, eur } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Reveal, Eyebrow } from "../components/Reveal";

const MESI_IT = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];

function formatMese(m) {
  if (!m) return "—";
  const [y, mm] = m.split("-");
  return `${MESI_IT[parseInt(mm, 10) - 1]} ${y}`;
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface border border-gold/40 px-4 py-3 shadow-sm">
      <p className="text-xs uppercase tracking-[0.2em] text-ink-muted">{label}</p>
      <p className="font-serif text-2xl text-ink mt-1">{eur(payload[0].value)}</p>
    </div>
  );
}

export default function AreaPersonale() {
  const { user, logout } = useAuth();
function FragmentRow({ client: c, index: i, open, chartData, onToggle }) {
  return (
    <>
      <tr
        className="border-b border-pine/10 last:border-0 cursor-pointer hover:bg-pine-soft/40 transition-colors"
        data-testid={`area-diretto-${i}`}
        onClick={onToggle}
        aria-expanded={open}
      >
        <td className="py-4 pr-4 font-serif text-xl text-ink whitespace-nowrap">
          <span className="inline-block mr-2 text-gold text-sm align-middle">{open ? "−" : "+"}</span>
          {c.nome} {c.cognome}
        </td>
        <td className="py-4 pr-4 text-sm text-ink-muted">{c.email}</td>
        <td className="py-4 pr-4 text-sm text-ink-muted whitespace-nowrap">{formatMese(c.ingresso)}</td>
        <td className="py-4 pr-4 text-sm text-ink">{eur(c.capitale)}</td>
        <td className="py-4 pr-4 text-sm text-ink">{eur(c.totale_ritirato)}</td>
        <td className="py-4 pr-4 text-sm text-ink">{eur(c.totale_profitto)}</td>
        <td className="py-4 text-sm font-semibold text-pine">{eur(c.riconoscimento)}</td>
      </tr>
      {open && (
        <tr className="border-b border-pine/10 last:border-0" data-testid={`area-diretto-chart-${i}`}>
          <td colSpan={7} className="py-5 pr-4">
            {chartData.length >= 2 ? (
              <div className="h-[150px] min-w-0">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 5, right: 8, left: 8, bottom: 0 }}>
                    <defs>
                      <linearGradient id={`fill-${i}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#C9A84C" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="#C9A84C" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="mese" tick={{ fill: "#506260", fontSize: 11, fontFamily: "Inter" }} axisLine={false} tickLine={false} />
                    <YAxis hide domain={["dataMin", "dataMax"]} />
                    <Tooltip content={<ChartTooltip />} cursor={{ stroke: "#C9A84C", strokeDasharray: "3 3" }} />
                    <Area type="monotone" dataKey="capitale" stroke="#0E5C59" strokeWidth={1.8} fill={`url(#fill-${i})`} dot={{ r: 2.5, fill: "#C9A84C", stroke: "#0E5C59", strokeWidth: 1 }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-sm text-ink-muted">Storico mensile non ancora disponibile per questo cliente.</p>
            )}
          </td>
        </tr>
      )}
    </>
  );
}


  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/area/overview")
      .then((res) => setData(res.data))
      .catch(() => toast.error("Impossibile caricare i dati dell'area personale."));
  }, []);

  const referralLink = data ? `${window.location.origin}/accedi?mode=registrati&ref=${data.referral_code}` : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copia non riuscita. Seleziona e copia il link manualmente.");
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  const downloadContract = async () => {
    try {
      const res = await api.get("/area/contract", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = data?.contract?.filename || "contratto.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download non riuscito. Riprova.");
    }
  };

  const stats = data
    ? [
        { label: "Capitale", value: eur(data.capitale), testid: "area-capitale" },
        { label: "Totale profitto", value: eur(data.totale_profitto), testid: "area-profitto" },
        { label: "Totale ritirato", value: eur(data.totale_ritirato), testid: "area-ritirato" },
        { label: "Mesi di lavorazione", value: String(data.mesi_lavorazione), testid: "area-mesi" },
        { label: "Clienti diretti", value: String(data.clienti_diretti), testid: "area-diretti" },
        { label: "Clienti indiretti", value: String(data.clienti_indiretti), testid: "area-indiretti" },
      ]
    : [];

  const chartData = (data?.performance || []).map((p) => ({
    mese: formatMese(p.mese).replace(" 20", " '"),
    capitale: p.capitale,
  }));

  const diretti = data?.clienti_diretti_elenco || [];
  const [openClient, setOpenClient] = useState(null);

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 lg:py-24" data-testid="area-personale">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <Eyebrow>Area personale</Eyebrow>
          <h1 className="mt-4 text-4xl sm:text-5xl leading-tight" data-testid="area-greeting">
            {user ? <>Benvenuto, <em className="italic text-gold">{user.nome}</em>.</> : "Benvenuto."}
          </h1>
          {data?.presentatore && (
            <p className="mt-3 text-sm text-ink-muted" data-testid="area-presentatore">
              Presentato da {data.presentatore}
            </p>
          )}
        </div>
        <button onClick={doLogout} className="btn-outline !px-5 !py-2.5" data-testid="area-logout">
          <LogOut size={15} /> Esci
        </button>
      </div>

      {!data ? (
        <p className="mt-16 text-ink-muted" data-testid="area-loading">Caricamento dei dati…</p>
      ) : (
        <>
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-3 gap-px bg-pine/10 border border-pine/10">
            {stats.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.05} className="h-full">
                <div className="bg-surface h-full p-7 sm:p-9">
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-ink-muted">{s.label}</p>
                  <p className="mt-3 font-serif text-3xl sm:text-4xl text-ink" data-testid={s.testid}>{s.value}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-10 card-surface p-8 sm:p-10" data-testid="area-performance-card">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-pine">Andamento del capitale</p>
              {chartData.length >= 2 ? (
                <div className="mt-6 h-[300px] sm:h-[340px] min-w-0" data-testid="area-performance-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ top: 10, right: 8, left: 8, bottom: 0 }}>
                      <defs>
                        <linearGradient id="capitaleFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#C9A84C" stopOpacity={0.35} />
                          <stop offset="100%" stopColor="#C9A84C" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="rgba(14,92,89,0.08)" vertical={false} />
                      <XAxis
                        dataKey="mese"
                        tick={{ fill: "#506260", fontSize: 12, fontFamily: "Inter" }}
                        axisLine={{ stroke: "rgba(14,92,89,0.2)" }}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fill: "#506260", fontSize: 12, fontFamily: "Inter" }}
                        axisLine={false}
                        tickLine={false}
                        width={70}
                        tickFormatter={(v) => `€${Math.round(v / 1000)}k`}
                      />
                      <Tooltip content={<ChartTooltip />} cursor={{ stroke: "#C9A84C", strokeDasharray: "3 3" }} />
                      <Area
                        type="monotone"
                        dataKey="capitale"
                        stroke="#0E5C59"
                        strokeWidth={2}
                        fill="url(#capitaleFill)"
                        dot={{ r: 3.5, fill: "#C9A84C", stroke: "#0E5C59", strokeWidth: 1.5 }}
                        activeDot={{ r: 5, fill: "#C9A84C", stroke: "#16201F" }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="mt-6 text-base text-ink-muted leading-relaxed" data-testid="area-performance-empty">
                  Lo storico mensile del capitale sarà disponibile qui non appena i dati saranno inseriti dalla struttura.
                </p>
              )}
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="mt-10 card-surface p-8 sm:p-10" data-testid="area-documents-card">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-pine">Documenti contrattuali</p>
              {data.contract ? (
                <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="font-serif text-2xl text-ink" data-testid="area-contract-name">{data.contract.filename}</p>
                    <p className="mt-1 text-sm text-ink-muted">
                      Caricato il {(data.contract.created_at || "").slice(0, 10).split("-").reverse().join("/")}
                    </p>
                  </div>
                  <button onClick={downloadContract} className="btn-outline" data-testid="area-contract-download">
                    <Download size={15} /> Scarica il contratto
                  </button>
                </div>
              ) : (
                <p className="mt-4 text-base text-ink-muted leading-relaxed" data-testid="area-contract-empty">
                  Il tuo contratto sarà disponibile qui non appena caricato dalla struttura.
                </p>
              )}
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-10 card-surface p-8 sm:p-10" data-testid="area-referral-card">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-pine">Il tuo link di presentazione</p>
              <p className="mt-3 text-base text-ink-muted leading-relaxed max-w-2xl">
                Condividi questo link con chi vuoi presentare: quando completerà la registrazione, lo
                ritroverai automaticamente tra i tuoi clienti diretti.
              </p>
              <div className="mt-5 flex flex-col sm:flex-row gap-3">
                <code className="flex-1 bg-cream border border-pine/15 px-4 py-3 text-sm text-ink break-all" data-testid="area-referral-link">
                  {referralLink}
                </code>
                <button onClick={copyLink} className="btn-outline shrink-0" data-testid="area-referral-copy">
                  {copied ? <><Check size={15} /> Copiato</> : <><Copy size={15} /> Copia link</>}
                </button>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="mt-10 card-surface p-8 sm:p-10" data-testid="area-diretti-card">
              <div className="flex flex-wrap items-baseline justify-between gap-4">
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-pine">I tuoi clienti diretti</p>
                <p className="text-sm text-ink-muted" data-testid="area-totale-riconoscimenti">
                  Riconoscimenti percepiti: <span className="font-serif text-2xl text-ink align-middle">{eur(data.totale_riconoscimenti)}</span>
                </p>
              </div>
              {diretti.length === 0 ? (
                <p className="mt-6 text-base text-ink-muted leading-relaxed" data-testid="area-diretti-empty">
                  Nessun cliente presentato finora. Condividi il tuo link di presentazione per iniziare.
                </p>
              ) : (
                <div className="mt-6 overflow-x-auto">
                  <table className="w-full min-w-[720px] text-left" data-testid="area-diretti-table">
                    <thead>
                      <tr className="border-b border-pine/15">
                        {["Cliente", "Email", "Ingresso", "Depositato", "Ritirato", "Profitto", "Tuo riconoscimento"].map((h) => (
                          <th key={h} className="py-3 pr-4 text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-ink-muted">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {diretti.map((c, i) => {
                        const open = openClient === c.email;
                        const cData = (c.performance || []).map((p) => ({
                          mese: formatMese(p.mese).replace(" 20", " '"),
                          capitale: p.capitale,
                        }));
                        return (
                          <FragmentRow
                            key={c.email}
                            client={c}
                            index={i}
                            open={open}
                            chartData={cData}
                            onToggle={() => setOpenClient(open ? null : c.email)}
                          />
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </Reveal>
        </>
      )}
    </div>
  );
}
